import { createFileRoute, Link } from "@tanstack/react-router";
import { pageMeta, ldScripts, breadcrumbLd, faqLd } from "@/lib/seo";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { ButtonLink } from "@/components/ui-ext/Button";
import { TOOLS, type Tool } from "@/content/tools";
import { SERVICES } from "@/content/services";
import { ArrowRight, Zap, ShieldCheck, Sparkles } from "lucide-react";

export const Route = createFileRoute("/tools/")({
  head: () => ({
    ...pageMeta({
      path: "/tools/",
      title: "Free SEO Tools — SERP Preview, Schema, GEO Scorer | Optimize Plus",
      description:
        "13 free, in-browser SEO tools from Austin's Optimize Plus: SERP preview, schema explorer, meta length checker, AI Search Readiness scorer, robots.txt tester, hreflang generator, and more. No sign-up, no email wall.",
      keywords: [
        "free SEO tools",
        "SERP preview tool",
        "schema.org tester",
        "meta length checker",
        "robots.txt tester",
        "AI search scorer",
        "AEO tools",
        "GEO tools",
        "Austin SEO tools",
      ],
    }),
    scripts: ldScripts(
      breadcrumbLd([
        { name: "Home", path: "/" },
        { name: "Tools", path: "/tools/" },
      ]),
      faqLd(TOOLS_FAQ),
    ),
  }),
  component: ToolsIndex,
});


const TOOLS_FAQ = [
  {
    q: "Are these tools really free, with no sign-up or email wall?",
    a: "Yes. Every tool in the toolkit runs entirely in your browser — nothing is sent to our servers, there is no account to create, and we do not gate results behind an email capture. You can bookmark any tool and reuse it as often as you like.",
  },
  {
    q: "Can I replace a full crawler audit with these browser tools?",
    a: "No, and we would not recommend trying to. Browser tools are excellent for spot-checking a single URL — a page's schema, meta length, or heading structure — but they cannot crawl thousands of URLs, follow redirect chains site-wide, or aggregate log-file data the way Screaming Frog, Sitebulb, or an enterprise crawler can. Use the toolkit for fast, page-level triage; use a crawler for site-wide technical audits.",
  },
  {
    q: "Which tool should I run first when auditing a page?",
    a: "Start with the SERP Preview and Meta Length Checker before you publish, since those catch truncation and framing issues that are cheapest to fix pre-launch. Then move to Technical checks (Schema Explorer, robots.txt Tester, heading structure) once the page is live, followed by the AI Search Readiness Scorer, and finally Local checks if the page targets a specific city or service area.",
  },
  {
    q: "Does a high AI Search Readiness score guarantee an AI Overview citation?",
    a: "No tool can guarantee a citation, because AI Overviews, ChatGPT, and Perplexity all use proprietary retrieval and ranking logic that changes without notice. A high score means your page has the structural and semantic signals that correlate with citation-worthy content — clear entity definitions, extractable answers, and clean schema — not a promise of placement.",
  },
  {
    q: "How often should I re-run the toolkit on the same page?",
    a: "Re-check after any meaningful edit — a title rewrite, a schema change, a URL slug update — and again on a quarterly cadence even if nothing changed, since Google's SERP display rules and AI-search behaviours shift independently of your content.",
  },
  {
    q: "I run results through several tools and they disagree slightly. Which one is right?",
    a: "Treat disagreement as a sign to look at the underlying page, not a bug in the tools. Character-count and pixel-width heuristics for meta length, for example, are approximations of what Google actually renders, and different tools round differently. Use the tools directionally to catch obvious problems, then verify anything borderline by checking how the page actually renders in a live SERP or AI answer.",
  },
];

const CATEGORY_ORDER: Tool["category"][] = [
  "AI Search",
  "On-Page",
  "Technical",
  "Content",
  "Local",
];

const CATEGORY_BLURB: Record<Tool["category"], string> = {
  "AI Search": "Score and shape pages for AI Overviews, ChatGPT, and Perplexity citations.",
  "On-Page": "Sharpen titles, meta, slugs, and share previews before you publish.",
  Technical: "Debug crawlers, schema, hreflang, and heading structure in the browser.",
  Content: "Pressure-test copy for clarity, density, and reader-grade.",
  Local: "Ship LocalBusiness schema and NAP-clean assets for Austin & multi-location brands.",
};

// Cross-link map — sends tool intent to the most relevant paid service.
const CATEGORY_TO_SERVICE: Record<Tool["category"], string> = {
  "AI Search": "aeo-geo",
  "On-Page": "content-strategy",
  Technical: "technical-seo",
  Content: "content-strategy",
  Local: "local-seo",
};

function ToolsIndex() {
  const grouped = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    tools: TOOLS.filter((t) => t.category === cat),
  })).filter((g) => g.tools.length > 0);

  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1}>
        {/* HERO — accurate count, trust chips, dual CTA */}
        <section className="container-page pt-24 pb-16">
          <EyebrowLabel>Free SEO tools · No sign-up</EyebrowLabel>
          <h1 className="mt-4 font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
            {TOOLS.length} sharp tools,
            <br />
            <span className="text-primary">zero friction.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed">
            The exact in-browser utilities our Austin SEO team reaches for every day — SERP
            previews, schema, hreflang, AI-search scoring, and more. No email wall, no upsell,
            no data leaves your browser.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <ButtonLink to={"/tools/$slug/" as any} params={{ slug: "geo-score" } as any} search={{} as any} size="lg">
              Try the AI Search Scorer <ArrowRight className="h-4 w-4" aria-hidden />
            </ButtonLink>
            <ButtonLink to={"/contact/" as any} params={{} as any} search={{ location: "", service: "" } as any} variant="outline" size="lg">
              Talk to an SEO strategist
            </ButtonLink>
          </div>
          <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-widest text-muted-foreground">
            <li className="inline-flex items-center gap-2"><Zap className="h-3.5 w-3.5 text-primary" aria-hidden /> Runs 100% client-side</li>
            <li className="inline-flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-primary" aria-hidden /> No account, no tracking cookies</li>
            <li className="inline-flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden /> Built by Austin SEO practitioners</li>
          </ul>
        </section>

        {/* GROUPED CATEGORIES with service cross-links */}
        {grouped.map((g) => {
          const service = SERVICES.find((s) => s.slug === CATEGORY_TO_SERVICE[g.category]);
          return (
            <section key={g.category} className="container-page pb-20" aria-labelledby={`cat-${g.category}`}>
              <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div>
                  <EyebrowLabel>{g.category}</EyebrowLabel>
                  <h2
                    id={`cat-${g.category}`}
                    className="mt-2 font-display text-3xl md:text-4xl tracking-tight"
                  >
                    {g.category} tools
                  </h2>
                  <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                    {CATEGORY_BLURB[g.category]}
                  </p>
                </div>
                {service && (
                  <Link
                    to="/services/$slug/"
                    params={{ slug: service.slug }}
                    className="group inline-flex items-center gap-2 text-sm text-primary hover:gap-3 transition-all"
                  >
                    Want us to run this at scale? See {service.title}
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                )}
              </div>

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {g.tools.map((t) => {
                  const Icon = t.icon;
                  return (
                    <Link
                      key={t.slug}
                      to="/tools/$slug/"
                      params={{ slug: t.slug }}
                      className="group flex flex-col rounded-2xl border border-border p-7 bg-card/30 hover:bg-card/50 hover:border-primary/40 hover:-translate-y-0.5 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20 group-hover:bg-primary/15 transition-colors">
                          <Icon className="h-6 w-6" aria-hidden />
                        </span>
                        {t.status === "beta" && (
                          <span className="rounded-full bg-primary/15 text-primary px-2 py-0.5 text-[10px] uppercase tracking-widest">
                            Beta
                          </span>
                        )}
                      </div>
                      <h3 className="mt-5 font-display text-2xl leading-tight tracking-tight">
                        {t.name}
                      </h3>
                      <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                        {t.tagline}
                      </p>
                      <span className="mt-auto pt-6 inline-flex items-center gap-2 text-sm text-primary group-hover:gap-3 transition-all">
                        Open tool <span aria-hidden>→</span>
                      </span>
                    </Link>
                  );
                })}
              </div>
            </section>
          );
        })}

        {/* MID-FUNNEL — three ways to work with us */}
        <section className="container-page pb-24" aria-labelledby="tools-funnel">
          <div className="rounded-3xl border border-border bg-card/30 p-8 md:p-12">
            <EyebrowLabel>From tool to engagement</EyebrowLabel>
            <h2 id="tools-funnel" className="mt-3 font-display text-3xl md:text-4xl tracking-tight">
              Three ways teams graduate from tools to programmes.
            </h2>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              <Link
                to="/services/$slug/"
                params={{ slug: "technical-seo" }}
                className="rounded-2xl border border-border bg-background p-6 hover:border-primary/40 transition-all"
              >
                <div className="text-[0.65rem] uppercase tracking-widest text-primary">Audit</div>
                <div className="mt-1 font-display text-xl">Technical SEO Audit</div>
                <p className="mt-2 text-sm text-muted-foreground">
                  We run every check in this toolbox across your full site, then prioritise the
                  fixes by traffic impact.
                </p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm text-primary">
                  Explore audit <ArrowRight className="h-4 w-4" aria-hidden />
                </span>
              </Link>
              <Link
                to="/services/$slug/"
                params={{ slug: "aeo-geo" }}
                className="rounded-2xl border border-border bg-background p-6 hover:border-primary/40 transition-all"
              >
                <div className="text-[0.65rem] uppercase tracking-widest text-primary">AI Search</div>
                <div className="mt-1 font-display text-xl">AEO / GEO Programme</div>
                <p className="mt-2 text-sm text-muted-foreground">
                  Go beyond the Readiness Scorer — a full workstream to earn AI Overview,
                  ChatGPT, and Perplexity citations.
                </p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm text-primary">
                  See programme <ArrowRight className="h-4 w-4" aria-hidden />
                </span>
              </Link>
              <Link
                to="/services/$slug/"
                params={{ slug: "local-seo" }}
                className="rounded-2xl border border-border bg-background p-6 hover:border-primary/40 transition-all"
              >
                <div className="text-[0.65rem] uppercase tracking-widest text-primary">Local</div>
                <div className="mt-1 font-display text-xl">Austin Local SEO</div>
                <p className="mt-2 text-sm text-muted-foreground">
                  LocalBusiness schema, GBP optimisation, and neighborhood-level content for
                  Austin and DFW multi-location brands.
                </p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm text-primary">
                  See local SEO <ArrowRight className="h-4 w-4" aria-hidden />
                </span>
              </Link>
            </div>
          </div>
        </section>


        {/* EDITORIAL — what the toolkit is, workflow, limits, FAQ */}
        <section className="container-page pb-20 max-w-3xl" aria-labelledby="tools-about">
          <EyebrowLabel>About the toolkit</EyebrowLabel>
          <h2 id="tools-about" className="mt-3 font-display text-3xl md:text-4xl tracking-tight">
            What this free toolkit is, and who it is built for
          </h2>
          <div className="mt-6 space-y-5 text-[1.02rem] leading-relaxed text-foreground/85">
            <p>
              This is the working set of in-browser utilities our Austin SEO team actually opens
              during client audits — not a marketing gimmick bolted onto a lead form. Every tool
              runs client-side, meaning the URL, meta tag, or schema block you paste in never
              leaves your machine. That makes the toolkit safe to use on pre-launch pages, client
              drafts under NDA, or competitor pages you would rather not route through a
              third-party server.
            </p>
            <p>
              The toolkit is built for three overlapping audiences. In-house marketers who need a
              fast second opinion before a page ships. Agency and freelance SEOs who want a
              reliable pre-flight check without paying for a full enterprise crawler licence. And
              site owners who are curious whether a page is actually eligible for the AI Overviews,
              ChatGPT citations, or local map packs their competitors are already winning. None of
              these tools require SEO expertise to operate, but interpreting the output correctly —
              covered below — does take some judgement.
            </p>
          </div>

          <h2 className="mt-14 font-display text-3xl md:text-4xl tracking-tight">
            A repeatable audit workflow: sequence the tools, don't run them at random
          </h2>
          <p className="mt-4 text-[1.02rem] leading-relaxed text-foreground/85">
            The value of a toolkit compounds when you use it in a fixed order rather than opening
            whichever tool feels relevant that day. Here is the sequence we run on almost every
            page audit, split into four checkpoints.
          </p>
          <div className="mt-8 space-y-8">
            <div>
              <h3 className="font-display text-xl tracking-tight text-primary">1. Pre-publish checks</h3>
              <p className="mt-2 text-[1.02rem] leading-relaxed text-foreground/85">
                Before a page goes live, run the SERP Preview and Meta Length Checker to confirm
                the title and description will not truncate on desktop or mobile, then check the
                slug and share preview so the page looks intentional the first time anyone links to
                it. Fixing framing issues here is nearly free; fixing them after a page has earned
                backlinks and rankings is not.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl tracking-tight text-primary">2. Technical checks</h3>
              <p className="mt-2 text-[1.02rem] leading-relaxed text-foreground/85">
                Once the page is live, move to the Schema Explorer to validate structured data,
                the robots.txt Tester to confirm the page is actually crawlable, the hreflang
                Generator if the page has international variants, and a heading-structure check to
                make sure H1–H3 tags form a logical outline rather than a styling shortcut.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl tracking-tight text-primary">3. AI-search checks</h3>
              <p className="mt-2 text-[1.02rem] leading-relaxed text-foreground/85">
                Run the AI Search Readiness Scorer next, since it depends on the technical layer
                being clean — a page with broken schema or an unclear heading outline will score
                poorly here regardless of how well the prose reads. Use the output to identify
                missing entity definitions, unanswered questions, and passages that are too vague
                to be extracted as a direct answer.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl tracking-tight text-primary">4. Local checks</h3>
              <p className="mt-2 text-[1.02rem] leading-relaxed text-foreground/85">
                If the page targets a specific city, neighbourhood, or service area, finish with
                the LocalBusiness schema tools and a NAP (name, address, phone) consistency pass.
                Local checks come last because they are usually the fastest to fix once the
                technical and content layers are already sound.
              </p>
            </div>
          </div>

          <h2 className="mt-14 font-display text-3xl md:text-4xl tracking-tight">
            What each category is good at — and where it falls short
          </h2>
          <div className="mt-8 space-y-6">
            <div className="rounded-2xl border border-border p-6">
              <h3 className="font-display text-lg tracking-tight">On-Page tools</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Good at: catching truncation, duplicate titles, and awkward slugs before publish.
                Bad at: telling you whether the title will actually earn clicks — that still
                requires SERP testing over time, since pixel width alone doesn't predict CTR.
              </p>
            </div>
            <div className="rounded-2xl border border-border p-6">
              <h3 className="font-display text-lg tracking-tight">Technical tools</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Good at: validating one page's schema, robots directives, or heading outline in
                seconds. Bad at: surfacing site-wide problems — orphan pages, redirect chains, or
                crawl-budget waste — which require a full crawl, not a single-URL check.
              </p>
            </div>
            <div className="rounded-2xl border border-border p-6">
              <h3 className="font-display text-lg tracking-tight">AI Search tools</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Good at: flagging content that is too vague, unstructured, or entity-poor to be
                extracted by an AI answer engine. Bad at: predicting an actual citation, because no
                external tool has access to Google's, OpenAI's, or Perplexity's live retrieval
                models — the score is a proxy, not a guarantee.
              </p>
            </div>
            <div className="rounded-2xl border border-border p-6">
              <h3 className="font-display text-lg tracking-tight">Content tools</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Good at: flagging bloated sentences, weak reading grade, or thin keyword coverage
                at the paragraph level. Bad at: judging whether the content is actually accurate,
                original, or useful — that still requires a human editor with subject knowledge.
              </p>
            </div>
            <div className="rounded-2xl border border-border p-6">
              <h3 className="font-display text-lg tracking-tight">Local tools</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Good at: generating clean LocalBusiness schema and catching obvious NAP
                mismatches on a single page. Bad at: auditing citation consistency across the
                dozens of directories, GBP listings, and aggregators a multi-location brand
                actually needs monitored.
              </p>
            </div>
          </div>

          <h2 className="mt-14 font-display text-3xl md:text-4xl tracking-tight">
            Browser tools versus crawler data: know the ceiling
          </h2>
          <p className="mt-4 text-[1.02rem] leading-relaxed text-foreground/85">
            Every tool in this toolkit inspects one URL at a time, using whatever a browser can
            see: rendered HTML, inline schema, response headers it can read, and the page's own
            robots meta tag. That is fundamentally different from what a dedicated crawler like
            Screaming Frog, Sitebulb, or a log-file analyser does — those tools request thousands
            of URLs in sequence, follow every internal link and redirect, and aggregate patterns
            across an entire domain. A browser tool cannot tell you that 4,000 product pages share
            a duplicate title template, or that your crawl budget is being wasted on faceted
            navigation URLs. It can only tell you about the one page in front of it. Treat the
            toolkit as a magnifying glass, not a satellite view — use it to zoom into a specific
            page, and pair it with a proper crawl whenever you need the full-site picture.
          </p>

          <h2 className="mt-14 font-display text-3xl md:text-4xl tracking-tight">
            How to read the output without over-fitting to a single score
          </h2>
          <p className="mt-4 text-[1.02rem] leading-relaxed text-foreground/85">
            It is tempting to chase a perfect score on any single tool, but scores are heuristics,
            not ranking factors Google or an AI model consumes directly. A 98/100 AI Search
            Readiness score does not outrank a 90/100 page that has stronger backlinks or fresher
            data — it simply means the 98 is closer to the structural ideal the scorer is modelling.
            Use each score as a diagnostic that points you toward a specific fix (a missing FAQ
            schema block, an ambiguous H2, a meta description that truncates on mobile), then
            re-check the page holistically rather than iterating on the number itself. If a fix the
            tool suggests would make the copy read worse for an actual human, keep the copy and
            skip the fix — the tools exist to serve the reader, not the other way round.
          </p>

          {/* FAQ */}
          <div className="mt-16">
            <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">
              Frequently asked questions
            </h2>
            <dl className="mt-6 space-y-6">
              {TOOLS_FAQ.map((f, i) => (
                <div key={i} className="rounded-2xl border border-border p-6">
                  <dt className="font-display text-lg tracking-tight">{f.q}</dt>
                  <dd className="mt-2 text-foreground/85 leading-relaxed">{f.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="container-page pb-32">
          <div className="rounded-3xl border border-border p-10 md:p-14 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent">
            <EyebrowLabel>Ready for the next step?</EyebrowLabel>
            <h2 className="mt-3 font-display text-3xl md:text-5xl tracking-tight max-w-3xl">
              Tools are the appetiser. The full engagement is where rankings actually move.
            </h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">
              Show us a URL and a target keyword. We'll come back with a 20-minute Loom walking
              through the exact gaps our tools surface — plus the fix order that matters.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <ButtonLink to={"/contact/" as any} params={{} as any} search={{ location: "", service: "" } as any} size="lg">
                Request a free teardown <ArrowRight className="h-4 w-4" aria-hidden />
              </ButtonLink>
              <ButtonLink to={"/case-studies/" as any} params={{} as any} search={{} as any} variant="outline" size="lg">
                See client results
              </ButtonLink>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
